import { z } from 'zod';
import * as R from 'remeda';

export const IntegrationSecretSchemaName = z.enum([
  'bullhornIntegrationSecret',
  'textUsIntegrationSecret',
  'microsoftDynamicsSecret',
  'salesforceSecret',
]);
export type IntegrationSecretSchemaName = z.infer<typeof IntegrationSecretSchemaName>;

// ------------------------------------------------------------------------------------------------------------------ //
//region Integration secrets

/**
 * Helper to get a _schemaVersion property
 * @param current - Current version number. Must be a positive integer
 */
function schemaVersionNumber(current: number) {
  if (!Number.isInteger(current) || current < 1) {
    throw new Error(`Invalid schemaVersionNumber: ${current}`);
  }
  return z.number().int().positive().max(current).default(current);
}

const SecretDataBase = z.object({
  _schemaName: IntegrationSecretSchemaName.optional(),
  _schemaVersion: z.number().int().optional(),
});

export const MASKED_SECRET_PLACEHOLDER = '********';

// Replaces a field's value with either an empty string or the masked placeholder
const redactFieldValue = (val: string | null | undefined) => (val ? MASKED_SECRET_PLACEHOLDER : '');

export const BullhornSecretCredentials = z.object({
  clientSecret: z.string().default('').meta({ isSecret: true }),
  password: z.string().default('').meta({ isSecret: true }),
  internalPassword: z.string().default('').meta({ isSecret: true }),
});
export type BullhornSecretCredentials = z.infer<typeof BullhornSecretCredentials>;

export const BullhornNonSecretCredentials = z.object({
  clientId: z.string().default('').meta({ isSecret: false }),
  username: z.string().default('').meta({ isSecret: false }),
  internalUsername: z.string().default('').meta({ isSecret: false }),
});
export type BullhornNonSecretCredentials = z.infer<typeof BullhornNonSecretCredentials>;

export const BullhornSecretValues = z.object({
  ...BullhornNonSecretCredentials.shape,
  ...BullhornSecretCredentials.shape,
});

export const BullhornSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('bullhornIntegrationSecret').default('bullhornIntegrationSecret'),
  _schemaVersion: schemaVersionNumber(1),
  ...BullhornSecretValues.shape,
});
export type BullhornSecretData = z.infer<typeof BullhornSecretData>;
export const BullhornRedactedSecretValues = BullhornNonSecretCredentials.safeExtend({
  redacted: z.literal(true),
  ...R.mapValues(BullhornSecretCredentials.shape, (field) => field.transform(redactFieldValue)),
});
export type BullhornRedactedSecretValues = z.infer<typeof BullhornRedactedSecretValues>;
export const BullhornCredentialsResponse = BullhornRedactedSecretValues.or(
  BullhornSecretValues.safeExtend({ redacted: z.literal(false).default(false) })
);
export const BullhornCredentialsInput = BullhornSecretValues.required().partial();

export const MSDynamicsNonSecretCredentials = z.object({
  clientId: z.guid().or(z.literal('')).default('').meta({ isSecret: false }),
});
export const MSDynamicsSecretCredentials = z.object({
  clientSecret: z.string().default('').meta({ isSecret: true }),
});
export const MSDynamicsCredentials = z.object({
  ...MSDynamicsNonSecretCredentials.shape,
  ...MSDynamicsSecretCredentials.shape,
});
export const MicrosoftDynamicsSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('microsoftDynamicsSecret').default('microsoftDynamicsSecret'),
  _schemaVersion: schemaVersionNumber(1),
  ...MSDynamicsCredentials.shape,
});
export type MicrosoftDynamicsSecretData = z.infer<typeof MicrosoftDynamicsSecretData>;

export const MSDynamicsCredentialsInput = MSDynamicsCredentials.required().partial();
export type MSDynamicsCredentialsInput = z.infer<typeof MSDynamicsCredentialsInput>;

export const MSDynamicsRedactedCredentials = MSDynamicsNonSecretCredentials.safeExtend({
  redacted: z.literal(true),
  ...R.mapValues(MSDynamicsSecretCredentials.shape, (field) => field.transform(redactFieldValue)),
});
export const MSDynamicsCredentialsResponse = MSDynamicsRedactedCredentials.or(
  MSDynamicsCredentials.safeExtend({ redacted: z.literal(false).default(false) })
);

// The consumer key and secret of an External Client App using the client credentials flow
export const SalesforceNonSecretCredentials = z.object({
  clientId: z.string().default('').meta({ isSecret: false }),
});
export const SalesforceSecretCredentials = z.object({
  clientSecret: z.string().default('').meta({ isSecret: true }),
});
export const SalesforceCredentials = z.object({
  ...SalesforceNonSecretCredentials.shape,
  ...SalesforceSecretCredentials.shape,
});
export const SalesforceSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('salesforceSecret').default('salesforceSecret'),
  _schemaVersion: schemaVersionNumber(1),
  ...SalesforceCredentials.shape,
});
export type SalesforceSecretData = z.infer<typeof SalesforceSecretData>;

export const SalesforceCredentialsInput = SalesforceCredentials.required().partial();
export type SalesforceCredentialsInput = z.infer<typeof SalesforceCredentialsInput>;

export const SalesforceRedactedCredentials = SalesforceNonSecretCredentials.safeExtend({
  redacted: z.literal(true),
  ...R.mapValues(SalesforceSecretCredentials.shape, (field) => field.transform(redactFieldValue)),
});
export const SalesforceCredentialsResponse = SalesforceRedactedCredentials.or(
  SalesforceCredentials.safeExtend({ redacted: z.literal(false).default(false) })
);

export const TextUsSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('textUsIntegrationSecret').default('textUsIntegrationSecret'),
  _schemaVersion: schemaVersionNumber(1),
  apiKey: z.string().default('').meta({ isSecret: true }),
  webhookSecret: z.string().default('').meta({ isSecret: true }),
});
export type TextUsSecretData = z.infer<typeof TextUsSecretData>;

// Validates any integration secret by its self describing _schemaName tag
export const IntegrationSecretData = z.discriminatedUnion('_schemaName', [
  BullhornSecretData,
  TextUsSecretData,
  MicrosoftDynamicsSecretData,
  SalesforceSecretData,
]);
export type IntegrationSecretData<
  T extends IntegrationSecretSchemaName = IntegrationSecretSchemaName,
> = z.infer<typeof IntegrationSecretData> & { _schemaName: T };

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
